import {
	autoUpdate,
	flip,
	offset,
	shift,
	useClientPoint,
	useDismiss,
	useFloating,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import { calculateAspectRatio, getIsPreviewable, PDF_ASPECT_RATIO } from "@msuite/utils/files";
import type { Nullable } from "@msuite/utils/types";
import { motion } from "motion/react";
import React, { useEffect, useState } from "react";
import { useDisclosure } from "../../hooks";
import { PDFViewer } from "../../pdf-viewer";
import { Portal } from "../../portal";

const MAX_WIDTH = 800;
const MAX_HEIGHT = 800;

/** Props Interface */
interface PreviewProps {
	children: React.ReactNode;
	file: Nullable<File>;
	height: Nullable<number>;
	width: Nullable<number>;
}

export function Preview({ children, file, width, height }: PreviewProps) {
	/** State */
	const [src, setSrc] = useState("");

	useEffect(() => {
		if (src) return;
		function handleOnLoadFileUrl() {
			if (file) {
				const fileUrl = URL.createObjectURL(file);
				setSrc(fileUrl);
			}
		}
		handleOnLoadFileUrl();
	}, [file]);

	/** Hooks */
	const disclosure = useDisclosure();
	const pdfViewerDisclosure = useDisclosure();
	const { refs, floatingStyles, context } = useFloating({
		open: disclosure.isOpen,
		onOpenChange: disclosure.setIsOpen,
		placement: "bottom-start",
		middleware: [
			shift({ padding: 20 }),
			flip(),
			offset({
				mainAxis: 15,
				crossAxis: 30,
			}),
		],
		whileElementsMounted: autoUpdate,
	});
	const hover = useHover(context);
	const dismiss = useDismiss(context);
	const clientPoint = useClientPoint(context, {});
	const { getFloatingProps, getReferenceProps } = useInteractions([hover, clientPoint, dismiss]);

	/** Guard */
	if (!getIsPreviewable(file?.type)) return children;

	/** Computed */
	const aspectRatio = calculateAspectRatio(width, height, PDF_ASPECT_RATIO);

	/** Functions */
	function handleOnFileClick() {
		if (!file?.type.includes("pdf")) return;
		pdfViewerDisclosure.onOpen();
	}

	/** Render */
	return (
		<React.Fragment>
			<motion.div
				ref={refs.setReference}
				{...getReferenceProps()}
				className="cursor-pointer"
				whileTap={{ scale: 0.99 }}
				onClick={handleOnFileClick}
			>
				{children}
			</motion.div>
			{disclosure.isOpen && (
				<Portal>
					<div className="fixed inset-0 pointer-events-none z-[1000]">
						<div
							key="file-preview"
							style={floatingStyles}
							{...getFloatingProps({ ref: refs.setFloating })}
						>
							<div
								style={{
									maxHeight: MAX_HEIGHT,
								}}
							>
								{file?.type.startsWith("image") ? (
									<img
										src={src}
										alt={file?.name}
										className="w-full h-full object-cover rounded-xl"
										style={{
											maxWidth: MAX_WIDTH,
											maxHeight: MAX_HEIGHT,
										}}
									/>
								) : (
									<object
										key="file-preview-object"
										data={`${src}#toolbar=0&navpanes=0&scrollbar=0`}
										type="application/pdf"
										width="100%"
										height="100%"
										style={{
											aspectRatio,
										}}
									>
										<p>{file?.name}</p>
									</object>
								)}
							</div>
						</div>
					</div>
				</Portal>
			)}
			{pdfViewerDisclosure.isOpen && (
				<PDFViewer
					isOpen={pdfViewerDisclosure.isOpen}
					onClose={pdfViewerDisclosure.onClose}
					url={src}
				/>
			)}
		</React.Fragment>
	);
}
